import React from "react";
import {
  IconAddressBook,
  IconFileDownload,
  IconPhoneIncoming,
  IconFileTypeZip,
  IconMap2,
} from "@tabler/icons-react";

import { InteractiveData } from "../../interfaces";
import { FormattedMessage } from "react-intl";

const getFilename = (url: string) => {
  try {
    const urlNoQuery = url.split("?")[0];
    const filename = urlNoQuery.substring(urlNoQuery.lastIndexOf("/") + 1);
    return filename;
  } catch (err) {
    return "File";
  }
};

function InteractiveContent({
  content,
  mode = "desktop",
  isSentMessage,
  scrollToBottom,
  openMedia,
}: {
  content: InteractiveData;
  mode?: String;
  isSentMessage: boolean;
  scrollToBottom?: (value: number) => void;
  openMedia?: Function;
}): JSX.Element {
  const [videoError, setVideoError] = React.useState(false);
  const component: { [key: string]: React.ReactNode | JSX.Element } =
    React.useMemo(
      () => ({
        audio: content.header?.audio?.url ? (
          <div className="interactive-audio">
            <audio controls>
              <source
                src={`https://s3.visitoai.com/${content.header.audio.url}`}
              />
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          <span>voice note</span>
        ),
        document: (() => {
          // Add another for zip
          const url = `https://s3.visitoai.com/${content.header?.document?.url}`;
          const extension = url.split(".").pop()?.toLowerCase();

          const iframePreviewEnabled = ["docx", "pdf", "xlsx"].includes(
            extension || ""
          );

          if (!iframePreviewEnabled) {
            return (
              <div className="interactive-document">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-visito-light btn-xs btn-flex"
                >
                  <IconFileTypeZip size={20} />
                  <span>
                    {content.header?.document?.filename ||
                      `Document.${extension || "bin"}`}
                  </span>
                </a>
              </div>
            );
          }
          const isPdf = extension === "pdf";
          return (
            <div
              className="interactive-document"
              onClick={() => openMedia?.()}
              style={{ position: "relative" }}
            >
              <div className="interactive-document-wrapper" />
              <>
                {isPdf ? (
                  <iframe
                    src={url}
                    title="Document file PDF"
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                ) : (
                  <iframe
                    title="Document file DOCX"
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                      url
                    )}&embedded=true`}
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                )}
              </>
            </div>
          );
        })(),
        file: (
          <div className="interactive-file flex flex-column gap-1 mb-1">
            <a
              href={content.header?.file?.url}
              target="_blank"
              rel="noreferrer"
              className="flex align-items-center"
            >
              <IconFileDownload size={28} />
              <span>{getFilename(content?.header?.file?.url)}</span>
            </a>
          </div>
        ),
        video: (
          <div className="interactive-video">
            {!videoError ? (
              <video
                onLoadStart={() => {
                  scrollToBottom?.(0);
                }}
                autoPlay={false}
                controls={true}
              >
                <source
                  src={
                    content.header?.video?.url?.includes("https://")
                      ? content.header?.video?.url
                      : `https://s3.visitoai.com/${content.header?.video?.url}`
                  }
                  type={content.header?.video?.mimeType}
                  onError={() => setVideoError(true)}
                  style={{ width: "100%", height: "auto" }}
                />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src="https://placehold.co/200x200?text=Content+no+longer+available"
                alt="Video failed to load"
              />
            )}
          </div>
        ),
        image: (
          <div className="interactive-img" onClick={() => openMedia?.()}>
            <img
              alt=""
              src={
                content.header?.image?.url?.includes("https://")
                  ? content.header?.image?.url
                  : `https://s3.visitoai.com/${content.header?.image?.url}`
              }
              onLoad={() => scrollToBottom?.(0)}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src =
                  "https://placehold.co/200x200?text=Content+no+longer+available";
              }}
            />
          </div>
        ),
        contacts: (
          <div className="interactive-contacts">
            {content.header?.contacts?.map((contact, index) => (
              <div key={index} className="interactive-contact">
                {contact.phones.map((p, i) => (
                  <button className="btn btn-visito-light btn-sm btn-flex">
                    <div className="interactive-contact-phone" key={i}>
                      <IconAddressBook size={20} />
                      <div className="interactive-contact-name">
                        {contact.name}
                      </div>
                      <a href={`tel:${p}`}>{p}</a>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        ),
        location: (
          <div className="interactive-location flex gap-2 align-items-center">
            <a
              className="btn btn-visito-light btn-sm btn-flex"
              href={`https://www.google.com/maps?q=${content.header?.location?.latitude},${content.header?.location?.longitude}`}
              target="_blank"
              rel="noreferrer"
            >
              <IconMap2 size={18} />
              <span className="interactive-location-name">
                {[
                  content.header?.location?.name || "Shared location",
                  content.header?.location?.address,
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </span>
            </a>
          </div>
        ),
        call: (
          <div>
            <div className="interactive-call">
              <div className="call-icon">
                <IconPhoneIncoming size={22} />
              </div>
              {content.header?.audio?.url ? (
                <audio controls>
                  <source
                    src={`https://s3.visitoai.com/${content.header?.audio?.url}`}
                  />
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <div className="fs-1 color-step-750">
                  <FormattedMessage id="interactive.call.active" />
                </div>
              )}
            </div>
            {!content.call?.active && (
              <div className="interactive-call-summary">
                <div className="fs-1 color-step-750">
                  <FormattedMessage id="interactive.call.summary" />
                </div>
                <hr />
              </div>
            )}
          </div>
        ),
      }),
      [content.header, isSentMessage]
    );

  return (component[content.header.type] as JSX.Element) || <></>;
}

export default InteractiveContent;
